/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavigableBreadcrumbLink } from "../../../components/page_breadcrumbs/navigable_breadcrumb_link";
import {
  Box,
  Breadcrumbs,
  FormControl,
  IconButton,
  InputAdornment,
  Select,
  Typography
} from "@mui/material";
import {
  StyledBreadCrumbContainer,
  StyledLink,
} from "../../user_management/styles";
import DropdownSearchBar from "../../../components/dorpdown_searchbar";
import { UpdateButton } from "../../../components/update_button";
import { SelectedMenuItem } from "../../../components/selected_menu_item";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { selectBaseStyles, viewTypographyStyles } from "../styles";
import { StyledInputLabel } from "../../network_guests/styles";
import api from "../../../api";
import authenticatedFetch from "../../../api/authenticatedFetch";
import ClearIcon from '@mui/icons-material/Clear';
import { ResetButton } from "../../../components/reset_button";
import { MenuHeading } from "../../../components/menu_heading";
import { DropdownInfiniteLoader } from "../../../components/dropdown_infinite_loader";
import { EmptyFilteredState } from "../../../components/empty_filtered_state";
import { IndexTable } from "../../../components/index_table";
import camelize from "../../../utils/camelize";
import moment from "moment/moment";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { datePickerStyles } from "../employee_insights/styles";
import dayjs from "dayjs";
import { useNotificationContext } from "../../../contexts/notificationContext";
import { RenderList } from "../../../components/render_list";
import { RenderDiscountCardsList } from "../../../components/render_discount_cards_list";
import { RenderJobList } from "../../../components/render_job_list";
import { buildQueryParams } from "../../../utils/buildQueryParams";
import { FilterContainer } from "../../../components/filter_container";
import { StyledMenuItem } from "../../../components/styled_menu_item";
import { SOURCE_OF_TRUTH_OPTIONS } from "../../../constants/filterOptions";
import { startCase } from 'lodash'
import { Query_Keys } from "../../../lib/queryKeys";
import SearchIcon from '@mui/icons-material/Search';
import { containsText, matchesNormalizedSearch, normalizeSearchTerm } from "../../../utils/normalizeSearchTerm";
import { StyledSearchBar } from "./styles";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { PerPage } from "../../../constants/perPage";
import {
  getDropdownNextPageParam,
  mergeUniqueByKey,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../../utils/dropdownInfiniteQuery";
import { useDebounce } from "../../../utils/useDebounce";
import { useInfiniteScrollObserver } from "../../../hooks/useInfiniteScrollObserver";
import { getSingleItemValue } from "../../../utils/getSingleItemValue";
import { isValidDayjsValue } from "../../../utils/isValidDayjsValue";
import { isValidCustomDateRange } from "../../../utils/isValidCustomDateRange";

const EMPTY_FILTERS = {
  restaurant_ids: [],
  restaurant_group_id: '',
  date_range: '',
  source_of_truth: '',
};

const ALL_VALUE = "All";

export const ArchivedEmployees = () => {
  const [filtersChanged, setFiltersChanged] = useState(false)
  const location = useLocation()
  const initialFilters = {
    restaurant_ids: location?.state?.restaurantId || [],
    restaurant_group_id: location?.state?.restaurantGroupId || '',
    date_range: '',
    source_of_truth: '',
  }
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [restaurantSearch, setRestaurantSearch] = useState("");
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState('')
  const [sourceOfTruthSearch, setSourceOfTruthSearch] = useState("")
  const [applyFilters, setApplyFilters] = useState(false)
  const [showResetAlert, setShowResetAlert] = useState(false)
  const [sortBy, setSortBy] = useState('')
  const [sortOrder, setSortOrder] = useState('desc')
  const showNotification = useNotificationContext();
  const [csvLoading, setCsvLoading] = useState(false)
  const [search, setSearch] = useState('')

  const normalizedSearchTerm = useMemo(() => normalizeSearchTerm(search), [search]);
  const debouncedSearchTerm = useDebounce(normalizedSearchTerm, 300);
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([])
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([])
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300)
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300)

  const Days = useMemo(() => [
    "Today",
    "Yesterday",
    "This Week",
    "Last 7 Days",
    "This Month",
    "Last Month",
    "Custom Date",
  ], []);

  const formattedFilters = (filterValues = appliedFilters) => {
    const params = {};
    const today = dayjs();

    // Date range handling
    if (filterValues?.dateFilter !== "Custom Date" && filterValues?.dateFilter && filterValues?.dateFilter !== '') {
      switch (filterValues.dateFilter) {
        case "Today":
          params.date_range = {
            from: today.format("DD/MM/YYYY"),
            to: today.format("DD/MM/YYYY"),
          };
          break;
        case "Last 7 Days":
          params.date_range = {
            from: today.subtract(7, "day").format("DD/MM/YYYY"),
            to: today.format("DD/MM/YYYY"),
          };
          break;
        case "Yesterday":
          params.date_range = {
            from: today.subtract(1, "day").format("DD/MM/YYYY"),
            to: today.subtract(1, "day").format("DD/MM/YYYY"),
          };
          break;
        case "This Week":
          params.date_range = {
            from: today.startOf("week").format("DD/MM/YYYY"),
            to: today.endOf("week").format("DD/MM/YYYY"),
          };
          break;
        case "This Month":
          params.date_range = {
            from: today.startOf("month").format("DD/MM/YYYY"),
            to: today.endOf("month").format("DD/MM/YYYY"),
          };
          break;
        case "Last Month":
          params.date_range = {
            from: today
              .subtract(1, "month")
              .startOf("month")
              .format("DD/MM/YYYY"),
            to: today.subtract(1, "month").endOf("month").format("DD/MM/YYYY"),
          };
          break;
      }
    } else if (filterValues?.dateFilter === "Custom Date" && filterValues?.customDateFrom && filterValues.customDateTo) {
      params.date_range = {
        from: dayjs(filterValues.customDateFrom).format("DD/MM/YYYY"),
        to: dayjs(filterValues.customDateTo).format("DD/MM/YYYY"),
      };
    }

    if (filterValues?.restaurant_ids) {
      params.restaurant_ids = filterValues?.restaurant_ids;
    }

    if (filterValues?.restaurant_group_id) {
      params.restaurant_group_id = filterValues?.restaurant_group_id;
    }

    if (filterValues?.source_of_truth) {
      params.source_of_truth = filterValues?.source_of_truth
    }
    return params;
  }

  const fetchRestaurantGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    });
    return response?.data?.map(normalizeRestaurantGroup) || [];
  };

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({ search: debouncedRestaurantGroupSearch }),
    queryFn: fetchRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchRestaurants = async ({ pageParam = 1 }) => {
    let res = await api.get("/v1/admin/dropdowns/restaurants_list", {
      params: {
        restaurant_group_ids: filters?.restaurant_group_id,
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
      }
    });
    return res?.data?.map(normalizeRestaurant) || [];
  };

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: filters?.restaurant_group_id,
      search: debouncedRestaurantSearch,
    }),
    queryFn: fetchRestaurants,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  })

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  })

  const fetchArchivedEmployees = async () => {
    const data = formattedFilters(appliedFilters)
    const response = await api.get('/v1/admin/employees/archived_employees', {
      params: {
        per_page: itemsPerPage,
        page: page,
        ...(debouncedSearchTerm ? { search: debouncedSearchTerm } : {}),
        ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
        ...data
      },
    })
    return { data: response?.data, pageCount: response?.headers["total-count"] }
  }

  const hasAppliedRestaurantFilter =
    appliedFilters?.restaurant_ids?.length > 0 ||
    !!appliedFilters?.restaurant_group_id;

  const { data: archivedEmployees, isFetching } = useQuery({
    queryKey: Query_Keys.archived_employees({
      queryFilter: appliedFilters,
      page: page,
      itemsPerPage: itemsPerPage,
      search: debouncedSearchTerm,
      sortBy,
      sortOrder,
    }),
    queryFn: fetchArchivedEmployees,
    staleTime: 10 * 60 * 1000,
    enabled: hasAppliedRestaurantFilter,
    placeholderData: keepPreviousData,
  })

  const handleSortChange = (nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy)
    setSortOrder(nextSortOrder)
    setPage(1)
  }

  const handleSearch = useCallback((searchQuery) => {
    setSearch(searchQuery)
    setPage(1)
  }, [])

  useEffect(() => {
    setRestaurantOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurants, 'id'));
  }, [restaurants]);

  useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurantGroups, 'label'));
  }, [restaurantGroups]);

  const hasSingleAvailableRestaurantGroup = !debouncedRestaurantGroupSearch && restaurantGroups.length === 1;
  const hasSingleAvailableRestaurant = !debouncedRestaurantSearch && restaurants.length === 1;

  const hasAppliedRestaurantGroup = !!appliedFilters?.restaurant_group_id;
  const hasAppliedRestaurant = !!appliedFilters?.restaurant_ids?.length;

  const canSearch =
    hasAppliedRestaurantGroup ||
    hasAppliedRestaurant ||
    hasSingleAvailableRestaurantGroup ||
    hasSingleAvailableRestaurant;

  const isSearchDisabled = !canSearch;

  useEffect(() => {
    if (hasSingleAvailableRestaurant) {
      setFilters({ ...filters, restaurant_ids: [restaurants?.[0]?.id] })
      setFiltersChanged(true)
    }
  }, [hasSingleAvailableRestaurant, restaurants])

  useEffect(() => {
    if (hasSingleAvailableRestaurantGroup) {
      setFilters({ ...filters, restaurant_group_id: restaurantGroups?.[0]?.label })
      setFiltersChanged(true)
    }
  }, [hasSingleAvailableRestaurantGroup, restaurantGroups])

  const getRestaurantGroupName = (id) => {
    const restaurantGroup = restaurantGroupOptionsCache?.find((res) => res.label === id)
    return restaurantGroup?.value ? restaurantGroup?.value : ''
  }

  const getRestaurantDisplayValue = (selected) => {
    if (selected?.length < 1) {
      return "No Restaurant Selected";
    }

    if (selected?.length === 1) {
      return restaurantOptionsCache?.find((res) => res.id === selected[0])?.name;
    }

    return `${selected?.length} Restaurants Selected`;
  }

  const filteredSourceOfTruth = SOURCE_OF_TRUTH_OPTIONS?.filter((source_of_truth) => {
    return matchesNormalizedSearch(source_of_truth?.label, sourceOfTruthSearch);
  })

  const handleSourceOfTruthChange = (event) => {
    setFiltersChanged(true)
    setFilters(prev => ({ ...prev, source_of_truth: event.target.value }))
  };

  const handleSourceOfTruthClear = () => {
    setFilters(prev => ({ ...prev, source_of_truth: '' }))
    setFiltersChanged(true);
  }

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      if (field === "dateFilter") {
        return {
          ...prev,
          dateFilter: value,
          customDateFrom: null,
          customDateTo: null,
        };
      }

      if (field === "customDateFrom") {
        if (!value) {
          return {
            ...prev,
            customDateFrom: null,
            customDateTo: null,
          };
        }

        return {
          ...prev,
          customDateFrom: value,
          customDateTo:
            isValidDayjsValue(value) && !isValidDayjsValue(prev.customDateTo)
              ? dayjs().startOf("day")
              : prev.customDateTo,
        };
      }

      return { ...prev, [field]: value };
    });
    setFiltersChanged(true);
  };

  const handleDropDownChange = (event) => {
    setFiltersChanged(true);
    setFilters(({ ...filters, restaurant_ids: event.target.value }));
  }

  const handleRestaurantGroupChange = (event) => {
    setFiltersChanged(true)
    setFilters((currentFilters) => ({
      ...currentFilters,
      restaurant_group_id: event.target.value,
      restaurant_ids: [],
    }));
  }

  const isCustomDateRangeValid = 
    filters?.dateFilter !== "Custom Date" ||
    isValidCustomDateRange(filters?.customDateFrom, filters?.customDateTo);

  const isUpdateDisabled =
    filters?.dateFilter === "Custom Date" &&
    !isCustomDateRangeValid;

  const handleDateRangeClear = () => {
    setApplyFilters(false)
    setFilters({
      ...filters,
      dateFilter: '',
      customDateFrom: null,
      customDateTo: null,
    });
    setFiltersChanged(true)
  }

  const handleRestaurantGroupClear = () => {
    setFiltersChanged(true)
    setFilters(({ ...filters, restaurant_group_id: '' }));
  }

  const handleRestaurantClear = () => {
    setFiltersChanged(true);
    setFilters(({ ...filters, restaurant_ids: [] }));
  }

  const handleChange = () => {
    if (filters?.restaurant_ids?.length > 0 || filters?.restaurant_group_id) {
      setApplyFilters(true)
      setShowResetAlert(false)
      setFiltersChanged(false)
      setAppliedFilters(filters)
      setItemsPerPage(25);
      setPage(1);
    } else {
      setAppliedFilters({})
      showNotification('Please select a restaurant group or restaurant', 'error')
      setFiltersChanged(false);
    }
  }

  const handleReset = useCallback(() => {
    setFiltersChanged(false);
    setApplyFilters(false);
    setShowResetAlert(true);
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setRestaurantSearch('');
    setRestaurantGroupSearch('');
    setSourceOfTruthSearch('');
    setSearch('');
    setSortBy('');
    setSortOrder('desc');
    setItemsPerPage(25);
    setPage(1);
  }, []);

  const handleExportCsv = async () => {
    try {
      setCsvLoading(true);
      const filterParams = formattedFilters(appliedFilters)
      const query = buildQueryParams({
        ...filterParams,
        ...(debouncedSearchTerm ? { search: debouncedSearchTerm } : {}),
      })
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=archived_employees&${query}`;
      console.log(url)

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      let date = new Date
      let filename = `archived_employees_${dayjs(date).format('YYYY-MM-DD')}.csv`

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);

      showNotification('CSV downloaded successfully', 'success');
    } catch (error) {
      console.error('CSV download error:', error);
      showNotification(error?.message || 'Failed to download CSV', 'error');
    } finally {
      setCsvLoading(false);
    }
  }

  const columnOptions = [
    { key: "id", sortable: true },
    { key: "name", sortable: true },
    { key: "email", sortable: true },
    { key: "phone_number", sortable: true },
    { key: "job_end_date", sortable: true },
    { key: "job_title", sortable: true },
    { key: "restaurant", sortable: true },
    { key: "source_of_truth", sortable: true },
    { key: "revoked_discount_cards", label: "Revoked Perq Cards", sortable: true, width: 500 },
  ];

  const columns = columnOptions?.map((column) => {
    return {
      key: column.key,
      label: column.label || camelize(column.key),
      sortable: column.sortable,
      width: column.width
    };
  });

  const TableRows = (data) => {
    return data?.map((record) => {
      return {
        id: record?.id,
        name: record?.full_name,
        email: record?.email,
        phone_number: record?.phone_number,
        job_end_date: moment.utc(record?.archived_at).format("MM/DD/YYYY"),
        job_title: {
          display: (
            <RenderJobList data={record?.job_titles} />
          ),
          sortValue: record?.job_titles?.[0]?.title
        },
        restaurant: record?.restaurant?.name,
        source_of_truth: {
          display: <RenderList data={record?.source_of_truth?.map((item) => startCase(item))} />,
          sortValue:  record?.source_of_truth?.[0]
        },
        revoked_discount_cards: {
          display: <RenderDiscountCardsList data={record?.revoked_discount_cards} />,
          sortValue: record?.revoked_discount_cards?.length
        },
      };
    });
  };

  const rows = TableRows(archivedEmployees?.data);
  const hasSearchTerm = !!debouncedSearchTerm;
  const showInitialLoader = isFetching && !hasSearchTerm;

  return (
    <React.Fragment>
      <Box sx={{
        height: "100%",
        width: "100%",
        overflowY: 'scroll',
        overflowX: 'hidden',
      }}
      >
        <StyledBreadCrumbContainer>
          <Breadcrumbs>
            <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
            <StyledLink color={"#515152"}>Reports</StyledLink>
            <StyledLink color={"#515152"}>Employee Reports</StyledLink>
            <StyledLink color={"#515152"}>Archived Employees Report</StyledLink>
          </Breadcrumbs>
        </StyledBreadCrumbContainer>

        <FilterContainer>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: { xs: 2, md: 0 }
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: "18px" },
                flexWrap: "wrap",
                width: { xs: "100%", md: "auto" },
                flex: 1
              }}
            >
              <React.Fragment>
                <Typography
                  variant="body2"
                  sx={{
                    ...viewTypographyStyles,
                    alignSelf: { xs: "flex-start", sm: "center" },
                    whiteSpace: "nowrap"
                  }}
                >
                  View
                </Typography>

                <FormControl
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: "160px" }
                  }}
                >
                  <StyledInputLabel>Restaurant Group</StyledInputLabel>
                  <Select
                    data-testid="archived-employees-restaurant-group-filter"
                    size="small"
                    label="Restaurant Group"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: {
                        sx: { maxHeight: "40vh" },
                        ref: restaurantGroupMenuRef,
                      },
                      autoFocus: false,
                      MenuListProps: { sx: { pt: 0 } },
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: { xs: "100%", sm: "160px" },
                      width: { xs: "100%", sm: "160px" },
                      borderColor: "#2b4fb9",
                    }}
                    onChange={handleRestaurantGroupChange}
                    onClose={() => setTimeout(() => setRestaurantGroupSearch(''), 1000)}
                    value={hasSingleAvailableRestaurantGroup ? getSingleItemValue(restaurantGroups)?.label : (filters?.restaurant_group_id || "")}
                    renderValue={() =>
                      hasSingleAvailableRestaurantGroup
                        ? getSingleItemValue(restaurantGroups)?.value
                        : getRestaurantGroupName(filters?.restaurant_group_id)
                    }
                    endAdornment={
                      filters?.restaurant_group_id && !hasSingleAvailableRestaurantGroup ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            onClick={handleRestaurantGroupClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : (
                        null
                      )
                    }
                  >
                    <DropdownSearchBar changeSearchText={setRestaurantGroupSearch} placeholder={'Search restaurant group'} isFetching={restaurantGroupFetching} />
                    {restaurantGroups.map((res) => (
                      <StyledMenuItem
                        key={res?.label}
                        value={res?.label}
                      >
                        {
                          hasSingleAvailableRestaurantGroup || filters?.restaurant_group_id === res?.label ? <SelectedMenuItem data={res?.value} /> : res?.value
                        }
                      </StyledMenuItem>
                    ))}
                    <DropdownInfiniteLoader
                      hasNextPage={hasNextRestaurantGroupsPage}
                      targetRef={restaurantGroupLoaderRef}
                    />
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: "200px" }
                  }}
                >
                  <Select
                    data-testid="archived-employees-restaurant-filter"
                    size="small"
                    displayEmpty
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: {
                        sx: { maxHeight: "40vh" },
                        ref: restaurantMenuRef,
                      },
                      autoFocus: false,
                      MenuListProps: { sx: { pt: 0 } },
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: { xs: "100%", sm: "200px" },
                      borderColor: "#2b4fb9",
                    }}
                    onChange={handleDropDownChange}
                    onClose={() => setTimeout(() => setRestaurantSearch(''), 1000)}
                    value={hasSingleAvailableRestaurant ? [getSingleItemValue(restaurants)?.id] : (filters?.restaurant_ids || [])}
                    multiple
                    renderValue={(selected) => hasSingleAvailableRestaurant ? getSingleItemValue(restaurants)?.name : getRestaurantDisplayValue(selected)}
                    endAdornment={
                      filters?.restaurant_ids?.length > 0 && !hasSingleAvailableRestaurant ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            onClick={handleRestaurantClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : (
                        null
                      )
                    }
                  >
                    <DropdownSearchBar changeSearchText={setRestaurantSearch} placeholder={'Search restaurant'} isFetching={restaurantFetching} />
                    {restaurants.map((res) => (
                      <StyledMenuItem
                        key={res?.id}
                        value={res?.id}
                      >
                        {
                          hasSingleAvailableRestaurant || filters?.restaurant_ids?.includes(res?.id) ? <SelectedMenuItem data={res?.name} /> : res?.name
                        }
                      </StyledMenuItem>
                    ))}
                    <DropdownInfiniteLoader
                      hasNextPage={hasNextRestaurantsPage}
                      targetRef={restaurantLoaderRef}
                    />
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: "160px" }
                  }}
                >
                  <StyledInputLabel>Date Range</StyledInputLabel>
                  <Select
                    value={filters?.dateFilter || ''}
                    renderValue={() =>
                      filters?.dateFilter
                    }
                    label='Date Range'
                    size="small"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: { sx: { width: "230px" } },
                      autoFocus: false,
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: { xs: "100%", sm: "160px" },
                      borderColor: "#2b4fb9",
                    }}
                    onChange={(e) => handleFilterChange("dateFilter", e.target.value)}
                    endAdornment={
                      filters?.dateFilter ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            onClick={handleDateRangeClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : (
                        null
                      )
                    }
                  >
                    {/* All option */}
                    <StyledMenuItem
                      value={ALL_VALUE}
                    >
                      {filters?.dateFilter === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                    </StyledMenuItem>
                    {Days.map((value) => (
                      <StyledMenuItem
                        value={value}
                        key={value}
                      >
                        {
                          filters?.dateFilter === value ? (
                            <SelectedMenuItem data={value} />
                          ) : (
                            <>
                              {value}
                            </>
                          )
                        }
                      </StyledMenuItem>
                    ))}
                  </Select>
                </FormControl>
                {filters?.dateFilter === "Custom Date" && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                      width: { xs: "100%", sm: "auto" }
                    }}
                  >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Typography variant="body2" sx={viewTypographyStyles}>
                        Days
                      </Typography>
                      <DatePicker
                        format="MM-DD-YYYY"
                        sx={{
                          ...datePickerStyles,
                          width: { xs: "100%", sm: "140px", lg: '160px' }
                        }}
                        slotProps={{ textField: { size: "small" } }}
                        value={filters?.customDateFrom || null}
                        onChange={(date) =>
                          handleFilterChange("customDateFrom", date)
                        }
                      />
                      <Typography variant="body2" sx={viewTypographyStyles}>
                        through
                      </Typography>
                      <DatePicker
                        sx={{
                          ...datePickerStyles,
                          width: { xs: "100%", sm: "140px", lg: '160px' }
                        }}
                        format="MM-DD-YYYY"
                        slotProps={{ textField: { size: "small" } }}
                        value={filters?.customDateTo || null}
                        onChange={(date) =>
                          handleFilterChange("customDateTo", date)
                        }
                      />
                    </LocalizationProvider>
                  </Box>
                )}

                <FormControl
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: "160px" }
                  }}
                >
                  <StyledInputLabel>Source of Truth</StyledInputLabel>
                  <Select
                    value={filters?.source_of_truth || ''}
                    renderValue={() =>
                      startCase(filters?.source_of_truth)
                    }
                    size="small"
                    label="Source of Truth"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: 160,
                      borderColor: "#2b4fb9",
                    }}
                    onChange={handleSourceOfTruthChange}
                    onClose={() => setTimeout(() => setSourceOfTruthSearch(''), 1000)}
                    endAdornment={
                      filters?.source_of_truth ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            onClick={handleSourceOfTruthClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : (
                        null
                      )
                    }
                  >
                    <DropdownSearchBar changeSearchText={setSourceOfTruthSearch} placeholder={'Search source of truth'} />
                    {
                      filteredSourceOfTruth
                        ?.filter((source_of_truth) =>
                          containsText(
                            `${source_of_truth?.label}`,
                            sourceOfTruthSearch
                          )
                        )
                        .map((item) => (
                          <StyledMenuItem
                            key={item?.value}
                            value={item?.value}
                          >
                            {
                              filters?.source_of_truth === item?.value ? <SelectedMenuItem data={item?.label} /> : item?.label
                            }
                          </StyledMenuItem>
                        ))
                    }
                  </Select>
                </FormControl>
              </React.Fragment>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                alignSelf: { xs: "flex-start", md: "center" },
                mt: { xs: 1, md: 0 },
                width: { xs: "100%", md: "auto" },
                justifyContent: { xs: "flex-start", md: "flex-end" }
              }}
            >

              <ResetButton
                onClick={handleReset}
                status={isFetching}
              />

              <UpdateButton
                onClick={handleChange}
                filtersChanged={filtersChanged}
                status={isFetching}
                disabled={isUpdateDisabled}
              />
            </Box>
          </Box>
        </FilterContainer>

        <MenuHeading
          report={false}
          dividerStyle={{ marginBottom: '16px' }}
          rightComponent={
            <StyledSearchBar
              value={search}
              placeholder="Search archived employees"
              disabled={isSearchDisabled}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onChange={(e) => handleSearch(e.target.value)}
              autoComplete="off"
              inputProps={{ 'data-testid': 'archived-employees-search-input' }}
            />
          }
        >
          Archived Employees Report
        </MenuHeading>

        <EmptyFilteredState
          showInitialLoader={showInitialLoader}
          showResetAlert={showResetAlert}
          debouncedSearchTerm={debouncedSearchTerm}
          applyFilters={applyFilters}
          emptyFilterLabel="Select a restaurant or restaurant group"
          emptyTable={
            <IndexTable
              columns={columns}
              data={[]}
              tableId="archived-employees"
              type="entries"
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              page={page}
              setPage={setPage}
              pageCount={0}
              report={false}
              showDownloadButton={true}
              disableDownloadButton={true}
              handleExportCsv={handleExportCsv}
              csvLoading={csvLoading}
              serverSort={true}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
              searchLoading={isFetching && !!debouncedSearchTerm}
              hasSearchTerm={!!debouncedSearchTerm}
              emptyStateText="No Data Exists"
            />
          }
        >
          {archivedEmployees?.data ? (
            <IndexTable
              columns={columns}
              data={rows}
              tableId="archived-employees"
              type="entries"
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              page={page}
              setPage={setPage}
              pageCount={archivedEmployees?.pageCount}
              report={false}
              showDownloadButton={true}
              disableDownloadButton={archivedEmployees?.data?.length === 0 || csvLoading}
              handleExportCsv={handleExportCsv}
              csvLoading={csvLoading}
              serverSort={true}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
              searchLoading={isFetching && !!debouncedSearchTerm}
              hasSearchTerm={!!debouncedSearchTerm}
              emptyStateText="No Data Exists"
            />
          ) : null}
        </EmptyFilteredState>
      </Box>
    </React.Fragment>
  )
}
